import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Container, Spinner, Button, Row, Col } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { joinClass, storeTransaction } from '../../api/classes';
import { getEventById, checkEventAccess } from '../../api/events';
import { EventRecordingsModal } from './components/EventRecordingsModal';
import { EventDocumentsModal } from './components/EventDocumentsModal';
import { EventStudentsModal } from './components/EventStudentsModal';
import { EventAttendanceModal } from './components/EventAttendanceModal';
import './EventDetail.scss';

const getUTCEventDate = (dateStr, timeStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const year = d.getUTCFullYear();
  const month = d.getUTCMonth();
  const date = d.getUTCDate();

  let hours = 0;
  let minutes = 0;
  if (timeStr) {
    const parts = timeStr.split(':');
    hours = parseInt(parts[0], 10) || 0;
    minutes = parseInt(parts[1], 10) || 0;
  }
  return new Date(Date.UTC(year, month, date, hours, minutes));
};

const formatDateTime = (date) => {
  if (!date || isNaN(date.getTime())) return 'TBD';
  const day = String(date.getDate()).padStart(2, '0');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'
  const formattedHours = String(hours).padStart(2, '0');

  return `${day} ${month} ${year} ${formattedHours}:${minutes} ${ampm}`;
};

export const EventDetail = () => {
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const id = location.state?.id || params.id;

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { user } = useSelector((state) => state.auth);
  const [showRecordings, setShowRecordings] = useState(false);
  const [showDocuments, setShowDocuments] = useState(false);
  const [showStudents, setShowStudents] = useState(false);
  const [showAttendance, setShowAttendance] = useState(false);
  const [isBooking, setIsBooking] = useState(false);
  const [showPaypal, setShowPaypal] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        setLoading(true);
        const res = await getEventById(id);
        const eventData = res.data || res.event || res;
        setEvent(eventData);

        try {
          const accessRes = await checkEventAccess(id);
          setHasAccess(accessRes.hasAccess || accessRes.data?.hasAccess || false);
        } catch (e) {
          console.warn("Failed to check event access", e);
          setHasAccess(false);
        }
      } catch (err) {
        console.error(err);
        setError('Failed to load event details.');
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [id]);

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  if (error || !event) {
    return (
      <Container className="py-5 text-center">
        <h4 className="text-danger">{error || 'Event not found'}</h4>
        <Button variant="primary" onClick={() => navigate(-1)} className="mt-3">Go Back</Button>
      </Container>
    );
  }

  // Determine if event is ended or live using UTC
  const startTimeStr = event.start_time || event.startTime || '00:00';
  const endTimeStr = event.end_time || event.endTime || '23:59';
  const eventDateStr = event.date || event.start_date || event.createdAt;

  const startDate = getUTCEventDate(eventDateStr, startTimeStr);
  const endDate = getUTCEventDate(eventDateStr, endTimeStr);

  const dateVal = event.startDate || event.start_date || event.date || event.createdAt;
  const endVal = event.endDate || event.end_date;

  const localStart = dateVal ? new Date(dateVal) : null;
  const localEnd = dateVal ? new Date(endVal || dateVal) : null;

  const isValidStart = localStart && !isNaN(localStart.getTime());
  const isValidEnd = localEnd && !isNaN(localEnd.getTime());

  const dateText = isValidStart
    ? localStart.toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' })
    : 'TBD';

  const startTimeText = isValidStart
    ? localStart.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : (event.start_time || event.startTime || 'TBD');

  const endTimeText = isValidEnd
    ? localEnd.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : (event.end_time || event.endTime || 'TBD');

  const now = new Date();
  const isEnded = event.completed; // endDate ? now > endDate : false;
  const isLive = event.started; // startDate && endDate ? (now >= startDate && now <= endDate) : false;
  const isWaiting = event.yet_to_Start;// startDate ? now < startDate : false;

  const isOwner = user && (
    (event.instructor && (event.instructor === user._id || event.instructor?._id === user._id)) ||
    (event.teacher && (event.teacher === user._id || event.teacher?._id === user._id)) ||
    (event.creator && (event.creator === user._id || event.creator?._id === user._id)) ||
    (event.community && (event.community.owner === user._id || event.community.owner?._id === user._id)) ||
    (event.community_owner === user._id)
  );

  const getValidUrl = (url) => {
    if (!url) return '#';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `https://${url}`;
  };

  const handleBook = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (event.eventType === 'paid') {
      setShowPaypal(true);
      return;
    }

    // Free class booking
    try {
      setIsBooking(true);
      await joinClass(id, { payment_method: 'free' });
      alert('Class joined successfully!');
      setHasAccess(true);
      const res = await getEventById(id);
      setEvent(res.data || res.event || res);
    } catch (err) {
      console.error('Failed to book class:', err);
      alert(err?.response?.data?.message || err?.message || 'Failed to book class.');
    } finally {
      setIsBooking(false);
    }
  };

  const joinUrl = event.joinLink || event.join_link || event.meetingLink || event.meeting_link || event.meetingUrl || event.meeting_url || event.zoomLink || event.zoom_link;

  return (
    <div className="event-mobile-view bg-white min-vh-100 d-flex flex-column">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between p-3">
        <button className="btn btn-link text-dark p-0" onClick={() => navigate(-1)}>
          <i className="bi bi-chevron-left fs-4"></i>
        </button>
        <h5 className="mb-0 fw-bold">Class Details</h5>
        <button className="btn btn-link text-dark p-0">
          <i className="bi bi-three-dots-vertical fs-4"></i>
        </button>
      </div>

      <Container className="flex-grow-1 p-3 pb-5">

        {/* Welcome Header */}
        <div className="text-center mb-4 mt-2">
          <h2 className="fw-bold d-inline-block position-relative">
            Welcome To Your Class!
            <div className="position-absolute w-100 bg-warning" style={{ height: '4px', bottom: '-8px', left: 0, borderRadius: '2px' }}></div>
          </h2>
        </div>

        {/* 2x2 Management Grid */}
        <Row className="g-3 mb-4">
          <Col xs={6}>
            <div className="card border-0 rounded-4 h-100 cd-grid-card">
              <div className="card-header bg-transparent border-0 pt-3 pb-0 d-flex align-items-center gap-2">
                <i className="bi bi-people-fill text-warning"></i>
                <span className="fw-semibold">Students</span>
              </div>
              <div className="card-body text-center d-flex flex-column align-items-center">
                <div className="cd-icon-circle mb-3">
                  <i className="bi bi-people-fill text-primary fs-2"></i>
                </div>
                <small className="text-muted mb-3 d-block">Manage Students Enrolled</small>
                <Button
                  className="w-100 rounded-pill fw-semibold border-0 cd-btn-orange mt-auto"
                  onClick={() => setShowStudents(true)}
                >
                  Assign Students
                </Button>
              </div>
            </div>
          </Col>
          <Col xs={6}>
            <div className="card border-0 rounded-4 h-100 cd-grid-card">
              <div className="card-header bg-transparent border-0 pt-3 pb-0 d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <i className="bi bi-mic-fill text-warning"></i>
                  <span className="fw-semibold">Recordings</span>
                </div>
                <i
                  className="bi bi-plus-circle-fill text-primary"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setShowRecordings(true)}
                ></i>
              </div>
              <div className="card-body text-center d-flex flex-column align-items-center">
                <div className="cd-icon-circle mb-3">
                  <i className="bi bi-mic-fill text-primary fs-2"></i>
                </div>
                <small className="text-muted mb-3 d-block">Manage Class Recordings</small>
                <Button
                  className="w-100 rounded-pill fw-semibold border-0 cd-btn-orange mt-auto"
                  onClick={() => setShowRecordings(true)}
                >
                  View Recordings
                </Button>
              </div>
            </div>
          </Col>
          <Col xs={6}>
            <div className="card border-0 rounded-4 h-100 cd-grid-card">
              <div className="card-header bg-transparent border-0 pt-3 pb-0 d-flex align-items-center gap-2">
                <i className="bi bi-calendar2-check text-warning"></i>
                <span className="fw-semibold">Attendance</span>
              </div>
              <div className="card-body text-center d-flex flex-column align-items-center">
                <div className="cd-icon-circle mb-3">
                  <i className="bi bi-calendar2-check text-primary fs-2"></i>
                </div>
                <small className="text-muted mb-3 d-block">Recent Attendance</small>
                <Button
                  className="w-100 rounded-pill fw-semibold border-0 cd-btn-orange mt-auto"
                  onClick={() => setShowAttendance(true)}
                >
                  View Attendance
                </Button>
              </div>
            </div>
          </Col>
          <Col xs={6}>
            <div className="card border-0 rounded-4 h-100 cd-grid-card">
              <div className="card-header bg-transparent border-0 pt-3 pb-0 d-flex align-items-center gap-2">
                <i className="bi bi-folder-fill text-warning"></i>
                <span className="fw-semibold">Class Documents</span>
              </div>
              <div className="card-body text-center d-flex flex-column align-items-center px-2">
                <div className="cd-icon-circle mb-2">
                  <i className="bi bi-folder-fill text-primary fs-2"></i>
                </div>
                <small className="text-muted mb-3 d-block" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>Homework, Videos, Quizzes & books...</small>
                <Button
                  className="w-100 rounded-pill fw-semibold border-0 cd-btn-orange mt-auto"
                  onClick={() => setShowDocuments(true)}
                >
                  View Documents
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {/* Status Badge */}
        <div className="text-center mb-4">
          {isEnded ? (
            <span className="badge rounded-pill border py-2 px-3 fw-normal border-danger text-danger bg-white">
              <i className="bi bi-x-circle-fill text-danger me-2"></i>Class Ended
            </span>
          ) : isLive ? (
            <span className="badge rounded-pill border py-2 px-3 fw-normal border-warning text-warning bg-white" style={{ borderColor: '#ffc107', color: '#ffc107' }}>
              <i className="bi bi-play-circle-fill text-warning me-2"></i>Class Live
            </span>
          ) : isWaiting ? (
            <span className="badge rounded-pill border py-2 px-3 fw-normal bg-white" style={{ borderColor: '#ff9f1c', color: '#ff9f1c' }}>
              <i className="bi bi-clock-history me-2" style={{ color: '#ff9f1c' }}></i>Waiting to Start
            </span>
          ) : (
            <span className="badge rounded-pill border py-2 px-3 fw-normal border-success text-success bg-white">
              <i className="bi bi-check-circle-fill text-success me-2"></i>Class Active
            </span>
          )}
        </div>

        {/* Action Buttons */}
        <div className="text-center mb-4 d-grid gap-2 col-10 mx-auto">
          {isOwner || hasAccess ? (
            <>
              {joinUrl ? (
                <Button
                  variant="primary"
                  className="rounded-pill fw-semibold py-2.5 shadow-sm cd-btn-orange border-0 text-white"
                  onClick={() => window.open(getValidUrl(joinUrl), '_blank')}
                  disabled={isEnded}
                >
                  <i className="bi bi-camera-video-fill me-2"></i> Join Class / Meeting
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  className="rounded-pill fw-semibold py-2.5"
                  disabled
                >
                  Join Link Not Available
                </Button>
              )}
            </>
          ) : showPaypal ? (
            <div className="mt-3 w-100">
              <PayPalScriptProvider options={{ "client-id": "AcEBlYxSTDKF51XKtEB_L4-X18Q4kpoyUj0yY6DggYqVCssqh9nJbcUESyx6Fp_HLJnPy3msNxmjttaG", currency: "USD" }}>
                <PayPalButtons
                  style={{ layout: "horizontal", height: 40 }}
                  createOrder={(data, actions) => {
                    return actions.order.create({
                      purchase_units: [{
                        amount: { value: (event.class_fees || event.cost || event.fee || 0).toString() }
                      }]
                    });
                  }}
                  onApprove={(data, actions) => {
                    return actions.order.capture().then(async (details) => {
                      try {
                        setIsBooking(true);
                        const txRes = await storeTransaction({
                          amount: event.class_fees || event.cost || event.fee || 0,
                          status: 'succeeded',
                          currency: 'usd',
                          charge_id: details.id,
                          payment_method: 'paypal',
                          transaction_type: 'class_enrollment'
                        });

                        if (txRes && (txRes.status === 200 || txRes.status === 'succeeded' || txRes.data)) {
                          await joinClass(id, { payment_method: 'paypal', transaction_id: details.id });
                          setShowPaypal(false);
                          setHasAccess(true);
                          alert('Class booked successfully!');
                          const res = await getEventById(id);
                          setEvent(res.data || res.event || res);
                        } else {
                          alert('Failed to store transaction. Please contact support.');
                          setShowPaypal(false);
                        }
                      } catch (err) {
                        console.error(err);
                        alert(err?.response?.data?.message || 'Payment successful but failed to join class on server.');
                      } finally {
                        setIsBooking(false);
                      }
                    });
                  }}
                  onCancel={() => setShowPaypal(false)}
                  onError={(err) => {
                    console.error("PayPal Error:", err);
                    alert("Payment failed or was cancelled.");
                    setShowPaypal(false);
                  }}
                />
              </PayPalScriptProvider>
            </div>
          ) : (
            <Button
              className="rounded-pill fw-semibold py-2.5 shadow-sm cd-btn-orange border-0 text-white"
              onClick={handleBook}
              disabled={isBooking}
            >
              {isBooking ? (
                <Spinner animation="border" size="sm" className="me-2" />
              ) : (
                <i className="bi bi-lightning-charge-fill me-1"></i>
              )}
              {event.eventType === 'paid' ? `Book Class – $${event.class_fees || event.cost || event.fee}` : 'Book Now'}
            </Button>
          )}
        </div>

        {/* Details Text */}
        <div className="mb-4">
          <h5 className="fw-bold mb-2">{event.title}</h5>
          <p className="text-muted mb-3">{event.description || 'No description provided.'}</p>
          <div className="d-flex flex-column gap-2 mb-3 bg-white border border-light-subtle p-3 rounded-3 shadow-sm">
            <div className="text-muted small">
              Access: <span className="text-dark fw-semibold">Public</span>
            </div>
            <div className="d-flex align-items-center gap-2 text-dark small fw-semibold">
              <i className="bi bi-tag-fill text-warning" style={{ color: '#ffb703' }}></i>
              <span>{event.eventType === 'paid' ? `Paid Class ($${event.class_fees || event.cost || event.fee})` : 'Free Class'}</span>
            </div>
            <div className="text-muted small fw-medium">
              {formatDateTime(localStart)} - {formatDateTime(localEnd)}
            </div>
          </div>

          {/* Schedule Repeats Panel */}
          {(event.is_recurrence || event.isRecurrence || event.isRecurring || (event.repeatType && event.repeatType !== 'onetime')) && (
            <div className="card border border-light-subtle rounded-4 p-3 mb-4 bg-light" style={{ backgroundColor: '#f8f9fa' }}>
              <h6 className="fw-bold d-flex align-items-center gap-2 mb-3 text-primary" style={{ color: '#0d6efd' }}>
                <i className="bi bi-arrow-repeat fs-5"></i> Repeats
              </h6>
              <div className="d-flex flex-column gap-2">
                <div className="d-flex align-items-center gap-2 text-dark small fw-medium">
                  <i className="bi bi-clock text-muted"></i>
                  <span>
                    Repeats every {event.repeatInterval || event.repeat_interval || 1} {event.repeatType === 'weekly' ? 'week' : event.repeatType === 'monthly' ? 'month' : 'week'}(s)
                  </span>
                </div>

                {/* Day Pills */}
                {(event.repeatType === 'weekly' || !event.repeatType) && (() => {
                  const classDays = event.repeatOn || event.repeat_on || event.selectedDays || event.selected_days || [];
                  const parsedClassDays = Array.isArray(classDays)
                    ? classDays
                    : (typeof classDays === 'string' ? JSON.parse(classDays) : []);
                  if (parsedClassDays.length === 0) return null;
                  return (
                    <div className="d-flex flex-wrap gap-2 my-2">
                      {parsedClassDays.map(day => (
                        <span
                          key={day}
                          className="badge px-3 py-2 rounded-pill fw-semibold text-primary"
                          style={{ backgroundColor: '#e5efff', color: '#0d6efd' }}
                        >
                          {day}
                        </span>
                      ))}
                    </div>
                  );
                })()}

                {/* End Date Condition */}
                <div className="d-flex align-items-center gap-2 text-muted small mt-1">
                  <i className="bi bi-flag text-muted"></i>
                  <span>
                    {event.repeatEndType === 'afterOccurrences' ? (
                      `Ends after ${event.repeatEndAfterOccurrences || event.occurrences || 0} occurrences`
                    ) : event.repeatEndType === 'onDate' ? (
                      `Ends on ${event.repeatEndDate ? new Date(event.repeatEndDate).toLocaleDateString('en-GB') : 'TBD'}`
                    ) : (
                      'No end date'
                    )}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </Container>

      {/* Modals */}
      <EventRecordingsModal
        show={showRecordings}
        onHide={() => setShowRecordings(false)}
        eventId={id}
        communityId={event.community?._id || event.community}
        isOwner={isOwner}
      />
      <EventDocumentsModal
        show={showDocuments}
        onHide={() => setShowDocuments(false)}
        eventId={id}
        communityId={event.community?._id || event.community}
        isOwner={isOwner}
      />
      <EventStudentsModal
        show={showStudents}
        onHide={() => setShowStudents(false)}
        eventId={id}
        isOwner={isOwner}
      />
      <EventAttendanceModal
        show={showAttendance}
        onHide={() => setShowAttendance(false)}
        eventId={id}
        isOwner={isOwner}
      />
    </div>
  );
};
